<script setup lang="ts">
  import {
    computed, onBeforeMount, onBeforeUnmount, ref, watch
  } from 'vue'
  import { onClickOutside, useElementBounding, useMediaQuery } from '@vueuse/core'

  import { PageTheme, useLayoutStore } from '@/stores/layout'
  import type { AnchorNavigationPage } from '@/types/AnchorNavigationPage'
  import type { AnchorNavigationTask } from '@/types/Task'
  import type { LongformText } from '@/types/LongformText'

  import AnchorNavigation from '@/components/anchor-navigation/AnchorNavigation.vue'
  import AnchorNavigationTemplateSection from '@/components/anchor-navigation/AnchorNavigationTemplateSection.vue'
  import AnchorNavigationTemplateTop from '@/components/anchor-navigation/AnchorNavigationTemplateTop.vue'
  import FocusHeader from '@/components/layout/FocusHeader.vue'
  import Layout from '@/components/layout/Layout.vue'
  import LongformTextBlock from '@/components/LongformTextBlock.vue'
  import AnchorTask from '@/components/task/AnchorNavigationTask.vue'
  import Typography from '@/components/common/Typography.vue'
  import AnchorNavigationTemplateSkeleton from '@/components/skeletons/AnchorNavigationTemplateSkeleton.vue'
  import { KsButton } from '@aschehoug/kloss'

  const props = withDefaults(defineProps<{
    content: AnchorNavigationPage
    topSectionTitle?: string
    topSectionSuperTitle?: string
    resourceGuide?: AnchorNavigationPage | null
    isAnchorTask?: boolean
  }>(), {
    topSectionTitle: 'Til lærer',
    topSectionSuperTitle: '',
    anchors: undefined,
    resourceGuide: null,
    sectionContentComponent: false,
  })

  const contentElement = ref(null)

  const { setTheme } = useLayoutStore()
  const { top } = useElementBounding(contentElement)
  const anchorNavigationMobileId = 'anchor-navigation-mobile'

  const visibleAnchors = ref<string[]>([])
  const highlightedAnchor = ref<string>('')
  const isMobileAnchorNavigationOpen = ref<boolean>(false)
  const isMobileOrTablet = useMediaQuery('(max-width: 1023px)')
  const focusHeader = ref(null)

  const onElementVisibility = (isVisible: boolean, anchor: string | undefined) => {
    if (!anchor) return
    if (isVisible) return visibleAnchors.value = [...visibleAnchors.value, anchor]
    return visibleAnchors.value = [...visibleAnchors.value.filter(value => value !== anchor)]
  }

  onBeforeMount(() => setTheme(props.isAnchorTask ? PageTheme.AnchorNavigationPage : PageTheme.TeacherGuideTheme))
  onBeforeUnmount(() => setTheme(PageTheme.Default))
  onClickOutside(focusHeader, () =>  isMobileAnchorNavigationOpen.value = false)

  const isTopGradientVisible = computed(() => top.value <= 96)

  watch(visibleAnchors, () => {
    // Avoid empty highlighted anchor value
    if (!visibleAnchors.value.length) return
    highlightedAnchor.value = anchorTargets.value.find(target => visibleAnchors.value.includes(target)) ?? ''
  })

  const isSingleSection = computed((() => props.content.sections.length <= 1))
  const anchorTargets = computed(() => props.content.anchors ? props.content.anchors : props.content.sectionTitles)
</script>

<template>
  <Layout is-footer-hidden>
    <template #header>
      <FocusHeader
        ref="focusHeader"
        :is-with-background="!isSingleSection"
      >
        <div
          v-if="!isSingleSection && isMobileOrTablet"
          class="anchor-wrapper-mobile"
        >
          <KsButton
            :aria-expanded="isMobileAnchorNavigationOpen"
            :aria-controls="anchorNavigationMobileId"
            variant="tertiary"
            shape="rounded"
            icon-right="chevron-down"
            class="anchor-toggle-button-mobile"
            @click="isMobileAnchorNavigationOpen = !isMobileAnchorNavigationOpen"
          >
            {{ highlightedAnchor }}
          </KsButton>
          <AnchorNavigation
            :id="anchorNavigationMobileId"
            :anchors="anchorTargets"
            :highlighted-anchor="highlightedAnchor"
            :hidden="!isMobileAnchorNavigationOpen"
            class="anchor-navigation-mobile"
            :on-click="() => isMobileAnchorNavigationOpen = false"
          />
        </div>
      </FocusHeader>
    </template>
    <div
      v-if="content"
      class="anchor-navigation-template"
      data-track-context="lærerveiledningsside"
    >
      <div class="anchor-wrapper">
        <AnchorNavigation
          v-if="!isSingleSection && !isMobileOrTablet"
          :anchors="anchorTargets"
          :highlighted-anchor="highlightedAnchor"
        />
      </div>
      <div class="content-wrapper">
        <AnchorNavigationTemplateTop
          v-bind="content"
          :super-title="topSectionSuperTitle"
          :title="topSectionTitle"
          :resource-guide="resourceGuide ?? undefined"
        />
        <AnchorNavigationTemplateSkeleton v-if="!content.sections || !content.sections.length" />
        <div
          v-else
          ref="contentElement"
          class="content"
        >
          <div
            v-if="isAnchorTask && isTopGradientVisible"
            class="top-gradient"
          />
          <AnchorNavigationTemplateSection
            v-if="content.introSection"
            is-anchor-section
            :on-visibility="() => undefined"
          >
            <AnchorTask
              v-bind="content.introSection as AnchorNavigationTask"
              :title="content.introSection?.title ?? ''"
              :super-title="content.introSectionLabel"
            />
          </AnchorNavigationTemplateSection>
          <AnchorNavigationTemplateSection
            v-for="(section, index) in content.sections"
            :id="anchorTargets[index]"
            :key="index"
            :on-visibility="(isVisible: boolean) => onElementVisibility(isVisible, anchorTargets[index])"
            :is-anchor-section="isAnchorTask"
          >
            <AnchorTask
              v-if="isAnchorTask"
              v-bind="section as AnchorNavigationTask"
              :title="content.sectionTitles[index]"
              :super-title="isSingleSection ? undefined : content.anchors?.[index]"
            />
            <template v-else>
              <Typography
                v-if="content.anchors?.[index] && !isSingleSection"
                :id="content.anchors?.[index]"
                variant="label"
                class="section-header-super-title"
              >
                {{ content.anchors?.[index] }}
              </Typography>
              <Typography
                v-if="content.sectionTitles[index]"
                variant="h2"
                class="section-header"
              >
                {{ content.sectionTitles[index] }}
              </Typography>
              <LongformTextBlock :element="{ richText: section } as LongformText" />
            </template>
          </AnchorNavigationTemplateSection>
          <div v-if="isAnchorTask" class="bottom-gradient" />
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped lang="scss">
  :deep(.longform-text) {
    margin: initial;

    @include vgs-media-mobile {
      padding: 0;
    }
  }

  .anchor-navigation-template {
    color: var(--app-theme-color);
    margin: 148px auto 120px 0;

    @include vgs-media-desktop {
      display: grid;
      grid-template-columns: 1fr minmax(auto, $vgs-max-width-content-large) minmax(40px, 1fr);
    }

    @include vgs-media-mobile {
      margin-top: 0
    }
  }

  .anchor-wrapper {
    margin-right: auto;
    padding: 0 40px;

    @include vgs-media-mobile-and-tablet {
      padding: 0 24px;
    }

    :deep(.anchor-navigation) {
      position: sticky;
      top: 148px;
    }
  }

  .anchor-wrapper-mobile {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .anchor-toggle-button-mobile {
    background-color: var(--color-yellow-10);
    color: var(--app-theme-color);
    width: fit-content;

    @include vgs-media-hover {
      background-color: var(--color-yellow-20);
    }

    :deep(.ks-icon) {
      transition: transform .3s;
    }

    &[aria-expanded="true"] {
      :deep(.ks-icon) {
        transition: transform .3s;
        transform: rotate(180deg);
      }
    }
  }

  .anchor-navigation-mobile {
    place-items: center;
    padding: 88px;
    overflow: hidden;
    transition: all .3s;

    :deep(.ks-button) {
      opacity: 1;
      transform: scaleX(1);
      transition-delay: .2s;
      width: 100%;
    }

    &[hidden] {
      height: 0;
      opacity: 0;
      padding: 0;
      visibility: hidden;
      transition: all .3s;

      :deep(.ks-button) {
        opacity: 0;
        transform: scaleX(0);
        transform-origin: left;
        transition: all .3s;
      }
    }
  }

  .content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 96px;
    max-width: $vgs-max-width-content-large;
    padding-bottom: 60vh;
  }

  .content {
    position: relative;

    @include vgs-media-desktop {
      .top-gradient,
      .bottom-gradient {
        filter: blur(64px);
        left: 0;
        width: 100%;
        z-index: 3;
      }

      .top-gradient {
        position: fixed;
        top: -64px;
        height: 148px;
        background: linear-gradient(to bottom, var(--color-green-10), 96%, transparent);
      }

      .bottom-gradient {
        position: fixed;
        bottom: -64px;
        height: 15vh;
        background: linear-gradient(to bottom, transparent, 4%, var(--color-green-10));
      }
    }

    @include vgs-media-tablet {
      margin: 0 auto;
      max-width: $vgs-max-width-content;
    }

    @include vgs-media-mobile {
      margin: 0 24px;
    }
  }

  :deep(.cms-a) {
    color: var(--color-red-40);
  }

  :deep(.text-image-element) {
    background-color: inherit;
    color: inherit;
  }

  :deep(.vgs-expand-element) {
    margin-bottom: 1px;

    @include vgs-media-tablet-and-desktop {
      width: $vgs-max-width-content;
    }

    button {
      background-color: var(--color-white);
      color: var(--color-red-40);

      &:hover {
        --ks-expand: var(--color-coral-20);
      }
    }

    h2 {
      margin: 0;
    }
  }
</style>
